import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

export interface CartItem {
    id: number;
    cartId: string; // Unique identifier for cart operations
    name: string;
    price: number;
    image: string;
    quantity: number;
    category?: string;
    is_subscription?: boolean;
    selected_variation?: {
        id: number;
        sku: string;
        attribute: string;
        value: string;
    } | null;
}

export interface Coupon {
    code: string;
    discount_type: 'percentage' | 'fixed_amount';
    discount_value: number;
    description?: string;
}

interface CartContextType {
    cart: CartItem[];
    addToCart: (product: any, isSubscription?: boolean, quantity?: number) => void;
    removeFromCart: (cartId: string) => void;
    updateQuantity: (cartId: string, quantity: number) => void;
    clearCart: () => void;
    total: number;
    discountAmount: number;
    finalTotal: number;
    appliedCoupon: Coupon | null;
    applyCoupon: (coupon: Coupon) => void;
    removeCoupon: () => void;
    itemCount: number;
    isCartOpen: boolean;
    setIsCartOpen: (isOpen: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cart, setCart] = useState<CartItem[]>([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);

    // Load from localStorage on mount
    useEffect(() => {
        try {
            const savedCart = localStorage.getItem('shopping_cart');
            if (savedCart) setCart(JSON.parse(savedCart));

            const savedCoupon = localStorage.getItem('applied_coupon');
            if (savedCoupon) setAppliedCoupon(JSON.parse(savedCoupon));
        } catch (e) {
            console.error("Error parsing cart/coupon from localStorage", e);
        }
    }, []);

    // Save to localStorage whenever cart changes
    useEffect(() => {
        localStorage.setItem('shopping_cart', JSON.stringify(cart));
        if (cart.length === 0) {
            setAppliedCoupon(null); // Clear coupon if cart is empty
            localStorage.removeItem('applied_coupon');
        }
    }, [cart]);

    useEffect(() => {
        if (appliedCoupon) localStorage.setItem('applied_coupon', JSON.stringify(appliedCoupon));
        else localStorage.removeItem('applied_coupon');
    }, [appliedCoupon]);

    const addToCart = (product: any, isSubscription: boolean = false, quantity: number = 1) => {
        setCart(prev => {
            const variationId = product.selected_variation ? product.selected_variation.id : 'novar';
            const uniqueCartId = `${product.id}-${variationId}-${isSubscription ? 'sub' : 'once'}`;

            const existingIndex = prev.findIndex(item => item.cartId === uniqueCartId);

            if (existingIndex >= 0) {
                const newCart = [...prev];
                newCart[existingIndex] = {
                    ...newCart[existingIndex],
                    quantity: newCart[existingIndex].quantity + quantity
                };
                return newCart;
            }

            let finalPrice = parseFloat(product.sale_price || product.price || 0);

            // Apply subscription discount if applicable
            if (isSubscription) {
                finalPrice = Math.round(finalPrice * 0.9);
            }

            return [...prev, {
                id: product.id,
                cartId: uniqueCartId,
                name: product.name + (isSubscription ? ' (Socio Premium)' : '') + (product.selected_variation ? ` - ${product.selected_variation.value}` : ''),
                price: finalPrice,
                image: product.image || product.main_image_url || 'https://via.placeholder.com/150',
                quantity: quantity,
                category: product.category_name,
                is_subscription: isSubscription,
                selected_variation: product.selected_variation || null
            }];
        });
        setIsCartOpen(true);
    };

    const removeFromCart = (cartId: string) => {
        setCart(prev => prev.filter(item => item.cartId !== cartId));
    };

    const updateQuantity = (cartId: string, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(cartId);
            return;
        }
        setCart(prev => prev.map(item =>
            item.cartId === cartId ? { ...item, quantity } : item
        ));
    };

    const clearCart = () => {
        setCart([]);
        setAppliedCoupon(null);
        localStorage.removeItem('shopping_cart');
        localStorage.removeItem('applied_coupon');
    };

    const applyCoupon = (coupon: Coupon) => {
        setAppliedCoupon(coupon);
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
    };

    // Totals Calculation
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    let discount = 0;

    if (appliedCoupon) {
        if (appliedCoupon.discount_type === 'percentage') {
            discount = subtotal * (appliedCoupon.discount_value / 100);
        } else {
            discount = appliedCoupon.discount_value;
        }
    }
    // Ensure discount doesn't exceed subtotal
    if (discount > subtotal) discount = subtotal;

    const finalTotal = subtotal - discount;
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CartContext.Provider value={{
            cart, addToCart, removeFromCart, updateQuantity, clearCart,
            total: subtotal,
            discountAmount: discount,
            finalTotal,
            appliedCoupon,
            applyCoupon,
            removeCoupon,
            itemCount, isCartOpen, setIsCartOpen
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) throw new Error('useCart must be used within a CartProvider');
    return context;
};
